import { useEffect, useMemo, useState } from "react";
import { fetchAsDataUrl } from "@/utils/fetch-as-data-url";
import { EncounterSummaryReport } from "../note-preview-types";

export interface UseInlineReportImages {
  report: EncounterSummaryReport | undefined;
  isResolvingImages: boolean;
}

interface ResolvedImages {
  from: EncounterSummaryReport;
  report: EncounterSummaryReport;
}

// html2canvas cannot paint cross-origin S3 images, so the logo, signature and clock drawing are inlined first.
export const useInlineReportImages = (
  source: EncounterSummaryReport | undefined
): UseInlineReportImages => {
  const [resolved, setResolved] = useState<ResolvedImages>();

  useEffect(() => {
    if (!source) return;
    let isCancelled = false;
    const inline = (url?: string | null) =>
      url ? fetchAsDataUrl(url) : Promise.resolve(undefined);

    Promise.all([
      inline(source.providerGroupLogo),
      inline(source.providerSignature),
      inline(source.clockDrawingUrl),
    ]).then(([providerGroupLogo, providerSignature, clockDrawingUrl]) => {
      if (isCancelled) return;
      setResolved({
        from: source,
        report: {
          ...source,
          providerGroupLogo,
          providerSignature,
          clockDrawingUrl,
        },
      });
    });
    return () => {
      isCancelled = true;
    };
  }, [source]);

  return useMemo(
    () => ({
      report: source ? resolved?.report : undefined,
      isResolvingImages: !!source && resolved?.from !== source,
    }),
    [source, resolved]
  );
};
